import { z } from "zod";

/**
 * Phases of an agent task, as the control plane reports them. `unknown` is shown when the control
 * plane cannot be reached, so a stored phase is never presented as live.
 */
export const agentTaskPhases = [
  "starting",
  "running",
  "idle",
  "recovering",
  "done",
  "cancelled",
  "failed",
  "unknown",
] as const;
export const agentTaskPhaseSchema = z.enum(agentTaskPhases);
export type AgentTaskPhase = z.infer<typeof agentTaskPhaseSchema>;

/** A control-plane repository id: `repo_` and a UUID. */
export const hubRepositoryIdSchema = z
  .string()
  .regex(/^repo_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

/** Agent work goes on a branch under `zworks/`, so it can never target a default branch. */
export const AGENT_BRANCH_PREFIX = "zworks/";
export const agentBranchSchema = z
  .string()
  .trim()
  .max(200)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/)
  .refine(
    (value) => value.startsWith(AGENT_BRANCH_PREFIX) && value.length > AGENT_BRANCH_PREFIX.length,
    {
      message: `The branch must start with ${AGENT_BRANCH_PREFIX}.`,
    },
  )
  .refine((value) => !value.includes(".."), { message: "The branch is not a safe ref." });

/** One repository an agent task may work on, as the start form offers it. */
export const agentTaskRepositorySchema = z.object({
  repositoryId: hubRepositoryIdSchema,
  owner: z.string(),
  name: z.string().min(1),
  defaultBranch: z.string().min(1),
});
export type AgentTaskRepository = z.infer<typeof agentTaskRepositorySchema>;

/**
 * `GET /api/v1/projects/:id/agent-tasks/options`: what the start form can choose from. The
 * `modelKeys` list holds live `model-key` credential references the run may meter against. It is
 * empty when the project holds none; the start form offers BYOK only then, and never the key
 * material. Older responses omit it, so it defaults to empty.
 */
export const agentTaskOptionsSchema = z
  .object({
    profile: z.string().min(1),
    branchPrefix: z.literal(AGENT_BRANCH_PREFIX),
    repositories: z.array(agentTaskRepositorySchema),
    modelKeys: z.array(z.string().min(1)).optional().default([]),
  })
  .strict();
export type AgentTaskOptions = z.infer<typeof agentTaskOptionsSchema>;

/**
 * `POST /api/v1/projects/:id/agent-tasks`. The client chooses a repository, a branch under `zworks/`,
 * and the prompt; never an image, a grant, or a limit. The request id makes a retry reuse the task.
 * `modelKeyRef` names a `model-key` credential: the run is metered from its turns but settles no
 * credits, and the key itself never leaves the credential store.
 */
export const startAgentTaskSchema = z.object({
  repositoryId: hubRepositoryIdSchema,
  branch: agentBranchSchema,
  prompt: z.string().trim().min(1).max(20_000),
  requestId: z.uuid(),
  modelKeyRef: z.string().trim().min(1).max(200).optional(),
});
export type StartAgentTask = z.infer<typeof startAgentTaskSchema>;

/** `POST /api/v1/projects/:id/agent-tasks/:taskId/feedback`. */
export const agentTaskFeedbackSchema = z.object({
  text: z.string().trim().min(1).max(20_000),
});
export type AgentTaskFeedback = z.infer<typeof agentTaskFeedbackSchema>;

/** One agent task the console started, as the list shows it. */
export const agentTaskSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  hubTaskId: z.string().min(1),
  repositoryId: hubRepositoryIdSchema,
  repositoryName: z.string(),
  branch: z.string().min(1),
  prompt: z.string(),
  phase: agentTaskPhaseSchema,
  createdAt: z.iso.datetime(),
  createdBy: z.uuid(),
  /**
   * The `model-key` credential the run meters against. Set only for BYOK runs, which settle no
   * credits; null for credit-billed runs. The key material itself never crosses this contract.
   */
  modelKeyRef: z.string().nullable().default(null),
});
export type AgentTask = z.infer<typeof agentTaskSchema>;

export const agentTaskListSchema = z.array(agentTaskSchema);
export type AgentTaskList = z.infer<typeof agentTaskListSchema>;

/** The recorded outcome of one agent turn. */
export const agentTurnSchema = z.object({
  turnId: z.string().min(1),
  status: z.string().min(1),
  summary: z.string().nullable(),
  error: z.string().nullable(),
  exitCode: z.number().int(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  recordedAt: z.iso.datetime(),
});
export type AgentTurn = z.infer<typeof agentTurnSchema>;

/**
 * `GET /api/v1/projects/:id/agent-tasks/:taskId`: the task with its live phase and its turns.
 * `live` is false when the control plane could not be reached, and then the phase is `unknown`.
 */
export const agentTaskDetailSchema = agentTaskSchema.extend({
  live: z.boolean(),
  turns: z.array(agentTurnSchema),
  creditCap: z.number().int().nonnegative().default(0),
  chargedCredits: z.number().int().nonnegative().default(0),
});
export type AgentTaskDetail = z.infer<typeof agentTaskDetailSchema>;

/** One file of a stored agent task result. */
export const agentTaskArtifactFileSchema = z.object({
  path: z.string().min(1).max(256),
  size: z.number().int().nonnegative(),
  mediaType: z.string(),
});

/**
 * `GET /api/v1/projects/:id/agent-tasks/:taskId/artifacts`: the sealed results the control plane stored
 * for the task, one revision per finished turn. Download a file at
 * `.../artifacts/:revisionId/file?path=<path>`.
 */
export const agentTaskArtifactSchema = z.object({
  artifactId: z.string().min(1),
  revisionId: z.string().min(1),
  sequence: z.number().int(),
  state: z.string().min(1),
  turnId: z.string(),
  createdAt: z.string(),
  files: z.array(agentTaskArtifactFileSchema),
});
export type AgentTaskArtifact = z.infer<typeof agentTaskArtifactSchema>;
export const agentTaskArtifactListSchema = z.array(agentTaskArtifactSchema);
